/**
 * ForwardHistoryModal — answers the reseller's "where do I buy this?"
 * question after a customer asks about a forwarded item.
 *
 * Two view modes:
 *
 *   "items" (default): flat, deduplicated list of every post the
 *     reseller has ever forwarded. One row per unique post, ordered
 *     by most-recent-forward-time. Search bar narrows by brand /
 *     title / SKU substring. This is the discoverability mode —
 *     "customer wants this product I forwarded somewhere, where do
 *     I buy it?".
 *
 *   "batches": chronological by-batch view. Useful for "what did I
 *     send today" auditing rather than item lookup.
 *
 * Each item row links straight to the source URL (Nordstrom Rack
 * etc.) — reseller clicks, lands on the retailer's page, orders.
 */
import { useState, useEffect, useMemo } from "react";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { Btn, Mono, Serif } from "./ui.jsx";
import { useT, plural } from "../i18n.jsx";

export function ForwardHistoryModal({ onClose, toast }) {
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [forwards, setForwards] = useState([]);
  const [view, setView] = useState("items");   // "items" | "batches"
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        // Load 100 most-recent batches. Past that we'd want pagination;
        // 100 batches × ~20 posts = ~2000 items, which the flat-list
        // search can handle smoothly in the browser.
        const list = await api.listForwards({ limit: 100 });
        setForwards(Array.isArray(list) ? list : []);
      } catch (e) {
        toast?.error?.(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Build the flat, deduplicated item list. For each unique post:
  //   - keep the latest forward timestamp (so the list sorts by recency)
  //   - count total forwards (useful "you forwarded this N times" badge)
  //   - capture every target channel it was sent to (for the by-channel
  //     hint in the row — "→ @buyergramlive, @sales")
  const flatItems = useMemo(() => {
    const byId = new Map();
    for (const batch of forwards) {
      for (const post of batch.posts || []) {
        if (!post.id) continue;
        const existing = byId.get(post.id);
        const ts = batch.createdAt || 0;
        if (!existing) {
          byId.set(post.id, {
            post,
            lastForwardedAt: ts,
            forwardCount: 1,
            channels: new Set(batch.targets || []),
          });
        } else {
          if (ts > existing.lastForwardedAt) existing.lastForwardedAt = ts;
          existing.forwardCount += 1;
          for (const t of batch.targets || []) existing.channels.add(t);
        }
      }
    }
    const arr = Array.from(byId.values());
    arr.sort((a, b) => b.lastForwardedAt - a.lastForwardedAt);
    return arr;
  }, [forwards]);

  // Search filter — applied only in items view. Matches against
  // brand/title/SKU (lowercased substring).
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return flatItems;
    return flatItems.filter(({ post }) => {
      const hay = `${post.brand ?? ""} ${post.title ?? ""} ${post.sourceUrl ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [flatItems, query]);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(20, 17, 13, 0.4)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 40, padding: 20,
      }}
    >
      <div style={{
        background: T_NEW.paperRaised,
        borderRadius: T_NEW.r3,
        maxWidth: 720, width: "100%",
        maxHeight: "90vh",
        overflowY: "auto",
        padding: 24,
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, gap: 12 }}>
          <div>
            <Serif size={T_NEW.displaySm} style={{ fontWeight: 500 }}>
              {t("forward.history.title")}
            </Serif>
            <Mono color={T_NEW.ink56} size={T_NEW.micro} style={{ display: "block", marginTop: 4 }}>
              {t("forward.history.subtitle")}
            </Mono>
          </div>
          <button
            onClick={onClose}
            aria-label={t("forward.cancel")}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: T_NEW.ink56, fontSize: 24, lineHeight: 1, padding: 4,
            }}
          >×</button>
        </div>

        {/* View toggle + search (search only matters in items view) */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
          <div style={{
            display: "inline-flex",
            background: T_NEW.paperSoft,
            border: `1px solid ${T_NEW.ink12}`,
            borderRadius: T_NEW.r1,
            padding: 2,
          }}>
            <ViewTab active={view === "items"} onClick={() => setView("items")}>{t("forward.history.tab.items")}</ViewTab>
            <ViewTab active={view === "batches"} onClick={() => setView("batches")}>{t("forward.history.tab.batches")}</ViewTab>
          </div>
          {view === "items" && (
            <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
              <span aria-hidden style={{
                position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                color: T_NEW.ink56, pointerEvents: "none",
              }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="6" cy="6" r="4.25" stroke="currentColor" strokeWidth="1.4"/>
                  <path d="M9.5 9.5 L12.5 12.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
              </span>
              <input
                type="search"
                placeholder={t("forward.history.search.placeholder")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{
                  width: "100%", padding: "6px 10px 6px 28px",
                  border: `1px solid ${T_NEW.ink12}`, borderRadius: T_NEW.r1,
                  fontFamily: T_NEW.fontSans, fontSize: T_NEW.meta,
                  background: T_NEW.paperRaised, color: T_NEW.ink,
                }}
              />
            </div>
          )}
        </div>

        {loading && <p style={{ color: T_NEW.ink56 }}>{t("forward.history.loading")}</p>}

        {!loading && forwards.length === 0 && (
          <p style={{ color: T_NEW.ink56, fontStyle: "italic", padding: "32px 0" }}>
            {t("forward.history.empty")}
          </p>
        )}

        {!loading && forwards.length > 0 && view === "items" && (
          <>
            <Mono color={T_NEW.ink56} size={T_NEW.micro} style={{ display: "block", marginBottom: 10 }}>
              {query
                ? t("forward.history.foundOf", { n: filteredItems.length, total: flatItems.length })
                : `${flatItems.length} ${t(plural(flatItems.length, "forward.history.count"))}`}
            </Mono>
            <div style={{ display: "grid", gap: 8 }}>
              {filteredItems.map((entry) => (
                <ItemRow key={entry.post.id} entry={entry} t={t} />
              ))}
            </div>
            {filteredItems.length === 0 && query && (
              <p style={{ color: T_NEW.ink56, fontStyle: "italic", padding: "16px 0" }}>
                {t("forward.history.noMatch", { q: query })}
              </p>
            )}
          </>
        )}

        {!loading && forwards.length > 0 && view === "batches" && forwards.map((batch) => (
          <BatchCard key={batch.id} batch={batch} t={t} />
        ))}
      </div>
    </div>
  );
}


// ─── Items view row (flat, deduplicated) ─────────────────────────────
function ItemRow({ entry, t }) {
  const { post, lastForwardedAt, forwardCount, channels } = entry;
  const ago = humanAgo(lastForwardedAt, t);
  const click = () => {
    if (post.sourceUrl) window.open(post.sourceUrl, "_blank", "noopener,noreferrer");
  };
  return (
    <button
      type="button"
      onClick={click}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 12px",
        background: T_NEW.paperSoft, borderRadius: T_NEW.r1,
        border: `1px solid ${T_NEW.ink12}`,
        cursor: post.sourceUrl ? "pointer" : "default",
        textAlign: "left", width: "100%",
        transition: "background 0.12s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = T_NEW.paperRaised; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = T_NEW.paperSoft; }}
    >
      {post.imageUrl && (
        <img
          src={post.imageUrl}
          alt=""
          style={{
            width: 56, height: 56, objectFit: "cover",
            borderRadius: T_NEW.r1, flexShrink: 0,
            background: T_NEW.paperRaised,
          }}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro, color: T_NEW.persimmon,
          textTransform: "uppercase", letterSpacing: T_NEW.monoTrack, fontWeight: 600,
        }}>
          {post.brand || t("forward.history.dash")}
        </div>
        <div style={{
          fontSize: T_NEW.body, color: T_NEW.ink, marginTop: 2,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {post.title || t("forward.history.dash")}
        </div>
        <Mono color={T_NEW.ink56} size={T_NEW.micro} style={{ marginTop: 4, display: "block" }}>
          {ago}
          {forwardCount > 1 && ` · ${t("forward.history.sendsCount", { n: forwardCount })}`}
          {channels.size > 0 && ` · ${Array.from(channels).join(", ")}`}
        </Mono>
      </div>
      {post.sourceUrl && (
        <span style={{
          color: T_NEW.ink56, fontSize: T_NEW.meta,
          fontFamily: T_NEW.fontMono, textTransform: "uppercase",
          letterSpacing: T_NEW.monoTrack, whiteSpace: "nowrap",
        }}>
          {t("forward.history.toSource")}
        </span>
      )}
    </button>
  );
}


function ViewTab({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: active ? T_NEW.ink : "transparent",
        color: active ? T_NEW.paperRaised : T_NEW.ink56,
        border: "none",
        padding: "5px 12px",
        borderRadius: T_NEW.r1,
        fontFamily: T_NEW.fontSans, fontSize: T_NEW.meta,
        cursor: "pointer", fontWeight: 500,
        transition: "background 0.12s, color 0.12s",
      }}
    >{children}</button>
  );
}


function BatchCard({ batch, t }) {
  const date = batch.createdAt
    ? new Date(batch.createdAt * 1000).toLocaleString("ru-RU", {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
      })
    : "";
  const okCount = countOk(batch.results);
  const totalCount = countTotal(batch.results);
  const tone =
    batch.status === "failed" ? T_NEW.persimmon
    : okCount < totalCount ? T_NEW.persimmon
    : T_NEW.ink56;

  return (
    <section style={{
      marginBottom: 16,
      paddingBottom: 16,
      borderBottom: `1px solid ${T_NEW.ink12}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <Mono color={T_NEW.ink56} size={T_NEW.micro}>
          {date} · {batch.targets?.join(", ") || t("forward.history.dash")}
        </Mono>
        <Mono color={tone} size={T_NEW.micro}>
          {okCount === totalCount && totalCount > 0
            ? t("forward.history.batchAllSent", { n: totalCount })
            : t("forward.history.batchPartial", { ok: okCount, total: totalCount })}
        </Mono>
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {(batch.posts || []).map((p) => (
          <PostRow key={p.id} post={p} t={t} />
        ))}
      </div>
    </section>
  );
}


// Russian relative-time formatter for the "last forwarded N ago"
// label. Mirrors the channel-feed humanAge() but kept local so the
// modal doesn't depend on ChannelFeed.jsx internals.
function humanAgo(epochSec, t) {
  if (!epochSec) return t("forward.history.dash");
  const seconds = Math.floor(Date.now() / 1000 - epochSec);
  if (seconds < 60) return t("forward.history.justNow");
  if (seconds < 3600) return `${Math.floor(seconds / 60)} ${t("forward.history.unitMin")}`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} ${t("forward.history.unitHour")}`;
  if (seconds < 7 * 86400) return `${Math.floor(seconds / 86400)} ${t("forward.history.unitDay")}`;
  return new Date(epochSec * 1000).toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}


function PostRow({ post, t }) {
  if (post.missing) {
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "8px 10px",
        background: T_NEW.paperSoft, borderRadius: T_NEW.r1,
        color: T_NEW.ink56, fontSize: T_NEW.meta, fontStyle: "italic",
      }}>
        <code style={{ fontSize: T_NEW.micro }}>{post.id.slice(0, 8)}</code>
        {t("forward.history.postMissing")}
      </div>
    );
  }
  const click = () => {
    if (post.sourceUrl) {
      window.open(post.sourceUrl, "_blank", "noopener,noreferrer");
    }
  };
  return (
    <button
      type="button"
      onClick={click}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "8px 10px",
        background: T_NEW.paperSoft, borderRadius: T_NEW.r1,
        border: `1px solid ${T_NEW.ink12}`,
        cursor: post.sourceUrl ? "pointer" : "default",
        textAlign: "left", width: "100%",
        transition: "background 0.12s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = T_NEW.paperRaised; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = T_NEW.paperSoft; }}
    >
      {post.imageUrl && (
        <img
          src={post.imageUrl}
          alt=""
          style={{
            width: 56, height: 56, objectFit: "cover",
            borderRadius: T_NEW.r1, flexShrink: 0,
            background: T_NEW.paperRaised,
          }}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro, color: T_NEW.persimmon,
          textTransform: "uppercase", letterSpacing: T_NEW.monoTrack, fontWeight: 600,
        }}>
          {post.brand || t("forward.history.dash")}
        </div>
        <div style={{
          fontSize: T_NEW.body, color: T_NEW.ink, marginTop: 2,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {post.title || t("forward.history.dash")}
        </div>
      </div>
      {post.sourceUrl && (
        <span style={{
          color: T_NEW.ink56, fontSize: T_NEW.meta,
          fontFamily: T_NEW.fontMono, textTransform: "uppercase",
          letterSpacing: T_NEW.monoTrack, whiteSpace: "nowrap",
        }}>
          {t("forward.history.toSource")}
        </span>
      )}
    </button>
  );
}


// Tally the per-post per-target results into a single "X of Y succeeded"
// summary. Defensive against unexpected shapes (older rows might be
// missing fields if we add columns later).
function countOk(results) {
  if (!results || typeof results !== "object") return 0;
  let n = 0;
  for (const perTarget of Object.values(results)) {
    if (!perTarget || typeof perTarget !== "object") continue;
    for (const r of Object.values(perTarget)) {
      if (r && r.ok) n++;
    }
  }
  return n;
}
function countTotal(results) {
  if (!results || typeof results !== "object") return 0;
  let n = 0;
  for (const perTarget of Object.values(results)) {
    if (!perTarget || typeof perTarget !== "object") continue;
    n += Object.keys(perTarget).length;
  }
  return n;
}
